import styles from './AccordionModule.module.scss';

import cx from 'classnames';
import type { Entry, ReferencedEntry } from 'contentful';
import React, { AllHTMLAttributes } from 'react';

import { TextAlignment, AccordionAssembly, Topic } from 'api/contentful/contentTypes.cloverdotcom';
import type { Entries } from 'api/contentful/utilTypes';
import { RichBlurb } from 'components/RichBlurb/RichBlurb';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { getField, isEntry } from 'utils/EntryUtil';
import {
  ButtonPosition,
  SimpleButtonAccordion,
} from '../Accordion/SimpleButtonAccordion/SimpleButtonAccordion';
import type { IAccordionItem } from '../Accordion/models';
import { Blurb } from '../Contentful/Blurb';
import { Text } from '../Contentful/Text';
import { EnhancedMedia } from '../EnhancedMedia/EnhancedMedia';
import { applyEntryDataAttributes } from '../EntryWrapper/EntryDataWrapper';
import { ButtonLinkV2 } from 'components/Button/ButtonLinkV2';
import { flattenContentfulData } from 'services/cms/flattenContentful';

interface IAccordionModuleProps extends AllHTMLAttributes<HTMLElement> {
  entry?: ReferencedEntry<AccordionAssembly>;
  isTabItem?: boolean;
}

const getAccordionItems = (
  containsImages: boolean,
  textAlignment: TextAlignment,
  isTabItem?: boolean,
  accordionItems: Entries<Topic> = [],
): IAccordionItem[] => {
  return accordionItems
    .filter(item => !!item && !!item.fields)
    .map((item: Entry<Topic>, index) => ({
      id: item.sys?.id,
      title: <Text entry={item} field="title" tagName="h5" className={styles.accordionRowTitle} />,
      headerClassName: cx(styles.accordionRowHeader, {
        [styles.accordionRowHeaderWithImages]: containsImages,
      }),
      defaultOpen: containsImages && index === 0, // have the first item open by default when images are there
      body: (
        <div
          className={cx(styles.accordionRowBody, {
            [styles.accordionRowBodyWithImages]: containsImages,
          })}
        >
          <RichBlurb
            document={getField(item, 'richBlurb')}
            className={cx(styles.blurb, {
              [styles.blurbWithImages]: containsImages,
              [styles.blurbTab]: isTabItem,
            })}
          />
          <Blurb
            entry={item}
            field="blurb"
            className={cx(styles.blurb, {
              [styles.blurbWithImages]: containsImages,
              [styles.blurbTab]: isTabItem,
            })}
          />
          <ButtonLinkV2
            entry={flattenContentfulData(item.fields.link)}
            className={cx(styles.contentLink)}
            isButton
          />
          {containsImages && (
            <div
              className={cx(styles.mediaWrapper, {
                [styles.mediaWrapperRight]: textAlignment === TextAlignment.RIGHT,
                [styles.mediaWrapperTab]: isTabItem,
              })}
            >
              <EnhancedMedia
                autoPlay
                loop
                muted
                preferWidth
                entry={item}
                className={cx(styles.mediaWrapperItem, {
                  [styles.mediaWrapperTabItem]: isTabItem,
                })}
              />
            </div>
          )}
        </div>
      ),
    }));
};

// if text alignment is center and the image is present, make the image as the background image of the container and not the enhanced media module
const AccordionModule: React.FC<IAccordionModuleProps> = ({ entry, className, isTabItem, ...props }) => {
  const { browser } = useBreakpoints() || {};
  const isDesktopOrLarge = browser?.is?.desktop;

  if (!isEntry(entry)) return null;

  const {
    items,
    backgroundColor,
    textAlignment = TextAlignment.LEFT,
    containsImages = false,
    alwaysHaveOneItemOpen = false,
    headingTagName = 'h2',
  } = entry.fields;

  return (
    <section className={cx(styles.moduleWrapper, className)} {...props} style={{ backgroundColor }}>
      <div
        className={cx(styles.container, {
          [styles.containerWithImages]: containsImages,
          [styles.containerFlexRight]: textAlignment === TextAlignment.RIGHT,
        })}
      >
        <div
          className={cx(styles.contentWrapper, {
            [styles.contentWrapperWithImages]: containsImages,
            [styles.contentWrapperFlexRight]: textAlignment === TextAlignment.RIGHT,
          })}
        >
          <div
            className={cx(styles.contentWrapperInner, {
              [styles.accordionWrapperRight]: textAlignment === TextAlignment.RIGHT,
            })}
          >
            <Text
              tagName={headingTagName}
              entry={entry}
              field="title"
              className={cx(styles.moduleTitle, {
                [styles.moduleTitleFullWidth]: containsImages,
                [styles.moduleTitleRight]: textAlignment === TextAlignment.RIGHT,
                [styles.noMargin]: isTabItem,
              })}
            />
            <Blurb entry={getField(entry, 'hero')} field="blurb" className={styles.heroBlurb} />
            <SimpleButtonAccordion
              items={getAccordionItems(containsImages, textAlignment, isTabItem, items)}
              buttonPosition={ButtonPosition.Left}
              alwaysHaveOneItemOpen={alwaysHaveOneItemOpen}
              allowMultipleOpen={!isDesktopOrLarge}
              className={cx(styles.accordion, {
                [styles.accordionWithImages]: containsImages,
                [styles.accordionRight]: textAlignment === TextAlignment.RIGHT,
                [styles.accordionTab]: isTabItem,
              })}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

const WrappedAccordionModule = applyEntryDataAttributes(AccordionModule);

export { WrappedAccordionModule, AccordionModule };
